import type { TechnicianAssignmentEntity } from '@packages/domain/techline/assignments';

import { Button, Icon, Typography } from '@packages/ui/shared';
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createUseStyles } from 'react-jss';

import { CollapsibleSection } from '../collapsible-section';
import { AssignmentCard } from './assignment-card';

/**
 * Props for AssignmentsSection.
 *
 * Presentational component that renders a collapsible list of technician
 * assignments with horizontal carousel navigation. All data fetching and
 * mutation logic is owned by the parent; this component only handles
 * layout, scroll, and expand/collapse UI state.
 */
export type AssignmentsSectionProps = {
    /** The list of technician assignments to display. Undefined while loading. */
    assignmentData?: TechnicianAssignmentEntity[];

    /** Whether the user can edit the submitted case. Users with the DSQM role cannot edit submitted cases. */
    canEditSubmittedCase: boolean;

    /** Whether assignments are currently being fetched. */
    isLoadingAssignments?: boolean;

    /** Whether the case is closed, disabling completion toggles. */
    isCaseClosed?: boolean;

    /**
     * Callback to refresh the assignment list from the API.
     *
     * @returns a promise that resolves when the refresh completes
     */
    onRefreshAssignments: () => Promise<void>;

    /**
     * Callback to toggle an assignment's completion status.
     *
     * @param assignmentId - the ID of the assignment to toggle
     * @returns a promise that resolves when the toggle completes
     */
    onToggleAssignmentCompletion: (assignmentId: string) => Promise<void>;
};

/** Inline styles for the scroll container that hides native scrollbars. */
const scrollContainerStyle: CSSProperties = {
    display: 'flex',
    gap: '15px',
    msOverflowStyle: 'none',
    overflowX: 'auto',
    scrollbarWidth: 'none',
};

/**
 * Renders a collapsible section containing technician assignment cards in a
 * horizontal carousel. Provides left/right scroll buttons when the
 * card list overflows.
 *
 * This is a presentational component; all data fetching and mutation logic
 * is delegated to the parent via callback props.
 *
 * @param props - {@link AssignmentsSectionProps}
 * @returns the assignments section JSX
 */
export function AssignmentsSection({
    assignmentData,
    canEditSubmittedCase,
    onToggleAssignmentCompletion,
    isCaseClosed,
}: AssignmentsSectionProps) {
    // State
    const [isExpanded, setIsExpanded] = useState(true);
    const [showLeftButton, setShowLeftButton] = useState(false);
    const [showRightButton, setShowRightButton] = useState(false);
    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    // Hooks
    const classes = useStyles();

    // Derived state
    const totalAssignments = assignmentData?.length || 0;
    const completedAssignments = assignmentData?.filter((a) => a.isCompleted).length || 0;

    const sortedAssignmentData = useMemo(() => {
        if (!assignmentData) {
            return [];
        }
        return [...assignmentData].sort(compareByCompletion).map((assignment, sortedIndex) => ({
            assignment: {
                label: `Assignment: ${assignment.name}`,
                details: assignment.description?.replace(/\n\n/g, '\n').split('\n') ?? [],
                ...assignment,
                completed: assignment.isCompleted,
            },
            key: `sorted-${sortedIndex}-${assignment.caseTechnicianAssignmentId || sortedIndex}`,
            sortedIndex,
        }));
    }, [assignmentData]);

    // Handlers

    /**
     * Check the scroll position of the carousel container and update
     * the visibility of the left and right navigation buttons.
     */
    const updateScrollButtonVisibility = useCallback(() => {
        if (!scrollContainerRef.current || !assignmentData || assignmentData.length === 0) {
            setShowLeftButton(false);
            setShowRightButton(false);
            return;
        }

        const container = scrollContainerRef.current;
        const containerRect = container.getBoundingClientRect();
        const cards = container.querySelectorAll('[data-index]') as NodeListOf<HTMLElement>;

        if (cards.length === 0) {
            setShowLeftButton(false);
            setShowRightButton(false);
            return;
        }

        const hasOverflow = container.scrollWidth > container.clientWidth;

        if (!hasOverflow) {
            setShowLeftButton(false);
            setShowRightButton(false);
            return;
        }

        // Check if the first and last cards are fully visible
        const firstCard = cards[0];
        const firstCardRect = firstCard.getBoundingClientRect();
        const isFirstElementFullyVisible = firstCardRect.left >= containerRect.left - 1;

        const lastCard = cards[cards.length - 1];
        const lastCardRect = lastCard.getBoundingClientRect();
        const isLastElementFullyVisible = lastCardRect.right <= containerRect.right + 1;

        setShowLeftButton(!isFirstElementFullyVisible);
        setShowRightButton(!isLastElementFullyVisible);
    }, [assignmentData]);

    /**
     * Scroll the carousel one card in the given direction. Finds the next
     * partially or fully hidden card and smoothly scrolls it into view.
     *
     * @param direction - which direction to scroll
     */
    const scrollAssignments = useCallback(
        (direction: 'left' | 'right') => {
            if (!scrollContainerRef.current) {
                return;
            }

            const container = scrollContainerRef.current;
            const containerRect = container.getBoundingClientRect();

            // Collect all visible cards with their visibility state
            const visibleElements: {
                /** The data-index attribute value of the card. */
                index: number;
                /** The card DOM element. */
                element: HTMLElement;
                /** The bounding client rect of the card. */
                rect: DOMRect;
                /** Whether the card is fully visible within the container. */
                fullyVisible: boolean;
            }[] = [];
            const cards = container.querySelectorAll('[data-index]') as NodeListOf<HTMLElement>;

            cards.forEach((card) => {
                const cardRect = card.getBoundingClientRect();
                const dataIndex = Number.parseInt(card.getAttribute('data-index') || '0');

                const isVisible = cardRect.right > containerRect.left && cardRect.left < containerRect.right;
                const isFullyVisible = cardRect.left >= containerRect.left && cardRect.right <= containerRect.right;

                if (isVisible) {
                    visibleElements.push({
                        index: dataIndex,
                        element: card,
                        rect: cardRect,
                        fullyVisible: isFullyVisible,
                    });
                }
            });

            if (visibleElements.length === 0) {
                return;
            }

            // Find the target card to scroll to
            const targetElement = findScrollTarget(container, containerRect, visibleElements, direction);
            if (!targetElement) {
                return;
            }

            // Calculate and apply the scroll offset
            const targetRect = targetElement.getBoundingClientRect();
            const scrollLeft = container.scrollLeft;
            const newScrollPosition =
                direction === 'right'
                    ? scrollLeft + (targetRect.right - containerRect.right) + 20
                    : scrollLeft + (targetRect.left - containerRect.left) - 20;

            container.scrollTo({ left: newScrollPosition, behavior: 'smooth' });
            setTimeout(updateScrollButtonVisibility, 300);
        },
        [updateScrollButtonVisibility]
    );

    /** Handle the left scroll button press. */
    const handleScrollLeftButtonPress = useCallback(() => {
        scrollAssignments('left');
    }, [scrollAssignments]);

    /** Handle the right scroll button press. */
    const handleScrollRightButtonPress = useCallback(() => {
        scrollAssignments('right');
    }, [scrollAssignments]);

    // Effects

    /** Initialize item refs array and update scroll button visibility after DOM updates. */
    useEffect(() => {
        const count = assignmentData?.length || 0;
        itemRefs.current = new Array(count).fill(null);

        setTimeout(updateScrollButtonVisibility, 100);
    }, [assignmentData?.length, updateScrollButtonVisibility]);

    /** Attach scroll and resize listeners to update carousel button visibility. */
    useEffect(() => {
        const container = scrollContainerRef.current;
        if (!container) {
            return;
        }

        container.addEventListener('scroll', updateScrollButtonVisibility);
        window.addEventListener('resize', updateScrollButtonVisibility);

        return () => {
            container.removeEventListener('scroll', updateScrollButtonVisibility);
            window.removeEventListener('resize', updateScrollButtonVisibility);
        };
    }, [assignmentData, updateScrollButtonVisibility]);

    return (
        <CollapsibleSection
            expanded={isExpanded}
            onExpandedChange={setIsExpanded}
            showDividerTop={false}
            title={
                <Typography variant="h3">
                    Assignments {totalAssignments > 0 ? `(${completedAssignments}/${totalAssignments})` : ''}
                </Typography>
            }
        >
            {totalAssignments > 0 ? (
                <div className={classes.assignmentContainer}>
                    {showLeftButton && (
                        <div className={classes.carousalButtonLeft}>
                            <Button onPress={handleScrollLeftButtonPress} variant="primary">
                                <Icon color="white" name="chevron-left" size={16} />
                            </Button>
                        </div>
                    )}
                    <div ref={scrollContainerRef} style={scrollContainerStyle}>
                        {sortedAssignmentData.map(({ assignment, key, sortedIndex }) => (
                            <AssignmentCard
                                assignment={assignment}
                                dataIndex={sortedIndex}
                                isCaseClosed={isCaseClosed}
                                key={key}
                                onToggleCompletion={onToggleAssignmentCompletion}
                                ref={(el: HTMLDivElement | null) => {
                                    itemRefs.current[sortedIndex] = el;
                                }}
                                showActionButton={canEditSubmittedCase}
                            />
                        ))}
                    </div>
                    {showRightButton && (
                        <div className={classes.carousalButtonRight}>
                            <Button onPress={handleScrollRightButtonPress} variant="primary">
                                <Icon color="white" name="chevron-right" size={16} />
                            </Button>
                        </div>
                    )}
                </div>
            ) : (
                <div className={classes.assignmentContainer}>
                    <AssignmentCard assignment={undefined} />
                </div>
            )}
        </CollapsibleSection>
    );
}

/**
 * Sorts assignments so that incomplete assignments appear before completed ones.
 * Assignments with the same completion status retain their relative order.
 *
 * @param a - the first assignment to compare
 * @param b - the second assignment to compare
 * @returns a negative number, zero, or positive number for sort ordering
 */
function compareByCompletion(a: TechnicianAssignmentEntity, b: TechnicianAssignmentEntity): number {
    if (a.isCompleted === b.isCompleted) {
        return 0;
    }
    return a.isCompleted ? 1 : -1;
}

/**
 * Finds the next card to scroll to based on the current visibility state.
 *
 * @param container - the scroll container element
 * @param containerRect - the bounding rect of the scroll container
 * @param visibleElements - the currently visible cards with their rects
 * @param direction - which direction to scroll
 * @returns the target element to scroll to, or null if no target exists
 */
function findScrollTarget(
    container: HTMLDivElement,
    containerRect: DOMRect,
    visibleElements: {
        /** The data-index attribute value of the card. */
        index: number;
        /** The card DOM element. */
        element: HTMLElement;
        /** The bounding client rect of the card. */
        rect: DOMRect;
        /** Whether the card is fully visible within the container. */
        fullyVisible: boolean;
    }[],
    direction: 'left' | 'right'
): HTMLElement | null {
    if (direction === 'right') {
        const rightmost = visibleElements[visibleElements.length - 1];
        if (!rightmost.fullyVisible) {
            return rightmost.element;
        }
        const nextCard = container.querySelector(`[data-index="${rightmost.index + 1}"]`) as HTMLElement;
        return nextCard || null;
    }

    const leftmost = visibleElements[0];
    if (!leftmost.fullyVisible) {
        return leftmost.element;
    }
    const prevIndex = leftmost.index - 1;
    if (prevIndex < 0) {
        return null;
    }
    return (container.querySelector(`[data-index="${prevIndex}"]`) as HTMLElement) || null;
}

const useStyles = createUseStyles({
    assignmentContainer: {
        display: 'flex',
        flexDirection: 'row',
        gap: '10px',
        position: 'relative',
    },
    carousalButtonLeft: {
        position: 'absolute',
        top: '0',
        left: '-24px',
        zIndex: 1,
        borderRadius: '4px',
        padding: '8px 12px',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.7) 100%)',
        transition: 'background 0.3s ease-in-out',
        '&:hover': {
            background: 'linear-gradient(to right, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.95) 100%)',
        },
        '& > div': {
            opacity: 0.6,
            transition: 'opacity 0.3s ease-in-out',
        },
        '&:hover > div': {
            opacity: 1,
        },
    },
    carousalButtonRight: {
        position: 'absolute',
        top: '0',
        right: '-24px',
        zIndex: 1,
        borderRadius: '4px',
        padding: '8px 12px',
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(to right, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.7) 100%)',
        transition: 'background 0.3s ease-in-out',
        '&:hover': {
            background: 'linear-gradient(to right, rgba(255, 255, 255, 0.2) 0%, rgba(255, 255, 255, 0.95) 100%)',
        },
        '& > div': {
            opacity: 0.6,
            transition: 'opacity 0.3s ease-in-out',
        },
        '&:hover > div': {
            opacity: 1,
        },
    },
});
